<template>
  <div class="relative w-full flex flex-1 overflow-hidden">
    <!-- <template v-if="$route.name !== 'home'">
      <img
        src="/images/capybara-head-lightgray.png"
        class="absolute top-0 left-0 size-[200px] sm:size-[300px] rotate-[36deg] translate-x-[-100px] translate-y-[40px] pointer-events-none z-[-1]"
        style="filter: brightness(0.95)"
        loading="lazy"
      />

      <img
        src="/images/capybara-head-lightgray.png"
        class="absolute bottom-0 right-0 size-[200px] sm:size-[300px] rotate-[-36deg] translate-x-[50px] translate-y-[-12px] pointer-events-none z-[-1]"
        style="filter: brightness(0.95)"
        loading="lazy"
      />
    </template> -->

    <div
      class="max-w-screen-sm w-full h-full mx-auto gap-4 flex flex-1 flex-col z-[1]"
      :class="{ 'overflow-auto': isPostAnswer || isResults }"
    >
      <div class="gap-4 flex flex-col">
        <ExercisesHeader
          :is-back-button="isBackButton"
          :section-slug="sectionSlug"
          :topic="topic"
          :is-demo="normalized.isDemo"
          @open-settings="handleOpenSettings"
        />

        <div class="px-4">
          <ExercisesProgress
            :index="exercises.activeIndex"
            :max="exercises.list.length"
          />
        </div>
      </div>

      <div
        v-if="topics.isLoading"
        class="flex flex-1 justify-center items-center"
      >
        <BaseLoader />
      </div>

      <template v-if="exercises.list.length">
        <ExercisesResults
          v-if="isResults"
          :is-demo="normalized.isDemo"
          :section-slug="sectionSlug"
          :topic-slug="topicSlug"
          @try-again="handleRefresh"
          @close-demo="handleCloseDemo"
        />

        <template v-else>
          <ExercisesPostAnswer
            v-if="isPostAnswer"
            :is-demo="normalized.isDemo"
            :section-slug="sectionSlug"
            :topic-slug="topicSlug"
            @close="handleClosePostAnswer"
            @open-settings="handleOpenSettings"
          />
          <ExercisesContent
            v-else
            @show-results="handleShowResults"
            @correct-answer="handleCorrectAnswer"
            @wrong-answer="handleWrongAnswer"
          />
        </template>
      </template>
    </div>

    <!-- Settings Modal -->
    <ExercisesSettingsModal
      v-if="isSettingsModalOpen"
      @close="handleCloseSettings"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import * as titleManager from '@/utils/title-manager'

import BaseLoader from '@/components/Base/BaseLoader.vue'

import ExercisesHeader from '@/components/Hub/Exercises/ExercisesHeader.vue'
import ExercisesProgress from '@/components/Hub/Exercises/ExercisesProgress.vue'

import ExercisesContent from '@/components/Hub/Exercises/ExercisesContent.vue'
// import ExercisesWrongAnswer from '@/components/Hub/Exercises/ExercisesWrongAnswer.vue'
import ExercisesPostAnswer from '@/components/Hub/Exercises/ExercisesPostAnswer.vue'
import ExercisesResults from '@/components/Hub/Exercises/ExercisesResults.vue'
import ExercisesSettingsModal from '@/components/Hub/Exercises/ExercisesSettingsModal.vue'

export default {
  name: 'HubExercises',

  components: {
    BaseLoader,

    ExercisesHeader,
    ExercisesProgress,

    ExercisesContent,
    ExercisesPostAnswer,
    ExercisesResults,
    ExercisesSettingsModal
  },

  props: {
    sectionSlug: { type: String, required: true },
    topicSlug: { type: String, required: true },

    isBackButton: { type: Boolean, default: true },
    // use only normalized.isDemo
    isDemo: { type: Boolean, default: false }
  },

  data: () => ({
    isPostAnswer: false,
    isResults: false,
    isSettingsModalOpen: false
  }),

  computed: {
    ...mapState('sections', ['sections']),
    ...mapState('topics', ['topics']),
    ...mapState('exercises', ['exercises', 'settings']),
    ...mapGetters('exercises', ['isFinished']),

    normalized() {
      return {
        isDemo: this.isDemo,
        isTikTok: localStorage.getItem('tiktok') === 'true'
      }
    },

    section() {
      return this.sections.list.find(
        section => section.slug === this.sectionSlug
      )
    },

    topic() {
      if (!this.section) return null

      const lightTopic = this.section.topics.find(
        topic => topic.slug === this.topicSlug
      )

      return this.topics.selected || lightTopic
    }
  },

  created() {
    if (this.section.slug === 'playground') {
      this.setTopicsSelected(this.topic)
      this.setExercisesList(this.topic.exercises)
      return
    }

    this.handleRefresh()
  },

  mounted() {
    titleManager.setTitle([`${this.section.name}: ${this.topic.name}`])
  },

  methods: {
    ...mapActions('topics', {
      fetchOneTopicBySlug: 'FETCH_ONE_BY_SLUG'
    }),
    ...mapMutations('topics', {
      setTopicsSelected: 'SET_SELECTED'
    }),
    ...mapMutations('exercises', {
      setExercisesList: 'SET_LIST',
      setActiveIndex: 'SET_ACTIVE_INDEX'
    }),

    handleCorrectAnswer() {
      if (this.normalized.isDemo || this.settings.isCorrectAnswerExplanations) {
        this.isPostAnswer = true
        return
      }

      this.isFinished
        ? this.handleShowResults()
        : this.setActiveIndex(this.exercises.activeIndex + 1)
    },

    handleWrongAnswer() {
      this.isPostAnswer = true
    },

    handleClosePostAnswer() {
      this.isPostAnswer = false

      if (this.isFinished) {
        this.handleShowResults()
        return
      }

      this.setActiveIndex(this.exercises.activeIndex + 1)
    },

    handleShowResults() {
      this.isResults = true
    },

    handleRefresh() {
      this.$gtag.event('exercise_started')

      this.setExercisesList([])
      this.isResults = false

      let limit = 10
      const localStorageLimit = localStorage.getItem('exercises-limit') || null

      if (localStorageLimit) {
        limit = Number(localStorageLimit)
      } else {
        limit = this.normalized.isDemo ? 5 : 10
      }

      const isOnlyHighQuality =
        this.normalized.isDemo || this.normalized.isTikTok

      return this.fetchOneTopicBySlug({
        topicSlug: this.topicSlug,
        isOnlyHighQuality,
        limit
      })
    },

    handleCloseDemo() {
      this.$emit('close-demo')
    },

    handleOpenSettings() {
      this.isSettingsModalOpen = true
    },

    handleCloseSettings() {
      this.isSettingsModalOpen = false
    }
  }
}
</script>
